<template>
  <section>
    <section
      v-for="(slice, index) in slices"
      :key="'slice-' + index"
      :class="index === slices.length - 1 ? 'overflow-auto' : ''"
    >
      <SlicesSeparator
        :margin-top="marginTop(index, slice.primary.margin_top, slice.slice_type)"
        :margin-bottom="marginBottom(slice.primary.margin_bottom, slice.slice_type)"
      >
        <!-- Collections Carousel template -->
        <template v-if="slice.slice_type === 'collections_carousel'">
          <CollectionsCarousel :slice="slice"></CollectionsCarousel>
        </template>

        <!-- FAQ template -->
        <template v-else-if="slice.slice_type === 'faq'">
          <FAQ :slice="slice"></FAQ>
        </template>

        <!-- Header template -->
        <template v-else-if="slice.slice_type === 'header'">
          <Header :slice="slice"></Header>
        </template>

        <!-- New Header template -->
        <template v-else-if="slice.slice_type === 'header_1'">
          <ExtendedHeader :slice="slice" />
        </template>

        <!-- Hero Header template -->
        <template v-if="slice.slice_type === 'hero_header'">
          <HeroHeader :slice="slice"></HeroHeader>
        </template>

        <!-- Hero Plans template -->
        <template v-if="slice.slice_type === 'hero_plans'">
          <HeroPlans :slice="slice"></HeroPlans>
        </template>

        <!-- Slider displaying two versions of the same image template -->
        <template v-else-if="slice.slice_type === 'mockup_slider'">
          <MockupSection :slice="slice"></MockupSection>
        </template>

        <!-- Niches & Topics Carousel template -->
        <template v-else-if="slice.slice_type === 'niches_carousel'">
          <NichesCarousel :slice="slice"></NichesCarousel>
        </template>

        <!-- Items featuring an image and some text as well as a cta in a grid template -->
        <template v-else-if="slice.slice_type === 'info_grid'">
          <InfoGrid :slice="slice"></InfoGrid>
        </template>

        <!-- Find Best Designs Block Template -->
        <template v-else-if="slice.slice_type === 'find_best_designs_blocks'">
          <FindBestDesignsBlocks :slice="slice" />
        </template>

        <!-- Plans template -->
        <template v-else-if="slice.slice_type === 'plans'">
          <Plans></Plans>
        </template>

        <!-- PODs template -->
        <template v-else-if="slice.slice_type === 'pod'">
          <PODs></PODs>
        </template>

        <!-- Purchase Plan template -->
        <template v-else-if="slice.slice_type === 'purchase_plan'">
          <PurchasePlan :slice="slice"></PurchasePlan>
        </template>

        <!-- Left Right Image template -->
        <template
          v-else-if="slice.slice_type === 'right_image' || slice.slice_type === 'left_image'"
        >
          <LeftRightImage
            :is-right="slice.slice_type === 'right_image'"
            :slice="slice"
          ></LeftRightImage>
        </template>

        <!-- Search Grid Or Carousel template -->
        <template v-else-if="slice.slice_type === 'search_grid'">
          <SearchGridCarousel :slice="slice"></SearchGridCarousel>
        </template>

        <!-- Styles Carousel template -->
        <template v-else-if="slice.slice_type === 'styles_carousel'">
          <StylesCarousel :slice="slice"></StylesCarousel>
        </template>

        <!-- Testimonials template -->
        <template v-else-if="slice.slice_type === 'testimonials'">
          <Testimonials :slice="slice"></Testimonials>
        </template>

        <!-- Text template -->
        <template v-else-if="slice.slice_type === 'text'">
          <TextColumns :slice="slice"></TextColumns>
        </template>

        <!-- Trusted by template -->
        <template v-else-if="slice.slice_type === 'trusted_by'">
          <TrustedBy></TrustedBy>
        </template>

        <!-- Vectors Grid template -->
        <template v-else-if="slice.slice_type === 'vectors_grid'">
          <VectorsGrid :slice="slice"></VectorsGrid>
        </template>

        <!-- Video template -->
        <template v-else-if="slice.slice_type === 'video'">
          <Video :slice="slice"></Video>
        </template>

        <!-- Hero with Tabs template -->
        <template v-else-if="slice.slice_type === 'hero_tabs'">
          <HeroTabs :slice="slice"></HeroTabs>
        </template>

        <!-- Plans compare table template -->
        <template v-else-if="slice.slice_type === 'compare_plans_table'">
          <PlansComparisonTable :slice="slice"></PlansComparisonTable>
        </template>

        <!-- Plans Get Graphics template -->
        <template v-else-if="slice.slice_type === 'plans_get_graphics'">
          <PlansGetGraphics :slice="slice"></PlansGetGraphics>
        </template>

        <!-- Plans Icon Block template -->
        <template v-else-if="slice.slice_type === 'plans_icon_block'">
          <PlansIconBlock :slice="slice"></PlansIconBlock>
        </template>

        <!-- Carousel Header template -->
        <template v-else-if="slice.slice_type === 'carousel_header'">
          <CarouselHeader :slice="slice"></CarouselHeader>
        </template>

        <!-- Carousel template -->
        <template v-else-if="slice.slice_type === 'carousel'">
          <Carousel :slice="slice"></Carousel>
        </template>

        <!-- Button Block template -->
        <template v-else-if="slice.slice_type === 'button'">
          <Button :slice="slice" />
        </template>

        <!-- How To Block template -->
        <template v-else-if="slice.slice_type === 'how_to'">
          <HowTo :slice="slice" />
        </template>

        <!-- Tabs Grid Experience template -->
        <template v-else-if="slice.slice_type === 'tabs_grid_experience'">
          <GridTabsExperience :slice="slice" />
        </template>

        <!-- Makers Block template -->
        <template v-else-if="slice.slice_type === 'makers'">
          <Makers />
        </template>

        <!-- You Can Find Block template -->
        <template v-else-if="slice.slice_type === 'you_can_find'">
          <YouCanFind :slice="slice" />
        </template>

        <!-- Title Block template -->
        <template v-else-if="slice.slice_type === 'title'">
          <Title :slice="slice" />
        </template>

        <!-- Columns Block template -->
        <template v-else-if="slice.slice_type === 'columns'">
          <Columns :slice="slice" />
        </template>

        <!-- Space Block template -->
        <template v-else-if="slice.slice_type === 'space'">
          <Space :slice="slice" />
        </template>

        <!-- Features Block template -->
        <template v-else-if="slice.slice_type === 'features'">
          <Features :slice="slice" />
        </template>

        <!-- Carousel Custom -->
        <template v-else-if="slice.slice_type === 'carousel_custom'">
          <CarouselCustom :slice="slice" />
        </template>

        <!-- Home Header Default -->
        <template v-else-if="slice.slice_type === 'home_header_default'">
          <ChooseExperience :slice="slice" />
        </template>

        <!-- Home Header Merch Design -->
        <template v-else-if="slice.slice_type === 'home_header_merch_design'">
          <BannerContent :slice="slice" />
        </template>
      </SlicesSeparator>
    </section>
  </section>
</template>

<script>
// Imports for all slices
import SlicesSeparator from '@/components/Prismic/Landings/SlicesSeparator';
const Carousel = () => import('@/components/Prismic/Landings/Slices/Carousel.vue');
const CarouselCustom = () => import('@/components/Prismic/Landings/Slices/CarouselCustom.vue');
const CarouselHeader = () => import('@/components/Prismic/Landings/Slices/CarouselHeader.vue');
const CollectionsCarousel = () =>
  import('@/components/Prismic/Landings/Slices/CollectionsCarousel.vue');
const FAQ = () => import('@/components/Prismic/Landings/Slices/FAQ.vue');
const Header = () => import('@/components/Prismic/Landings/Slices/Header.vue');
const ExtendedHeader = () => import('@/components/Prismic/Landings/Slices/ExtendedHeader.vue');
const HeroHeader = () => import('@/components/Prismic/Landings/Slices/HeroHeader.vue');
const HeroPlans = () => import('@/components/Prismic/Landings/Slices/HeroPlans.vue');
const FindBestDesignsBlocks = () =>
  import('@/components/Prismic/Landings/Slices/FindBestDesignsBlocks.vue');
const MockupSection = () => import('@/components/Prismic/Landings/Slices/MockupSection.vue');
const LeftRightImage = () => import('@/components/Prismic/Landings/Slices/LeftRightImage.vue');
const NichesCarousel = () => import('@/components/Prismic/Landings/Slices/NichesCarousel.vue');
const Plans = () => import('@/components/Prismic/Landings/Slices/Plans.vue');
const PODs = () => import('@/components/Prismic/Landings/Slices/PODs.vue');
const InfoGrid = () => import('@/components/Prismic/Landings/Slices/InfoGrid.vue');
const PurchasePlan = () => import('@/components/Prismic/Landings/Slices/PurchasePlan.vue');
const SearchGridCarousel = () =>
  import('@/components/Prismic/Landings/Slices/SearchGridCarousel.vue');
const StylesCarousel = () => import('@/components/Prismic/Landings/Slices/StylesCarousel.vue');
const Testimonials = () => import('@/components/Prismic/Landings/Slices/Testimonials.vue');
const TextColumns = () => import('@/components/Prismic/Landings/Slices/TextColumns.vue');
const TrustedBy = () => import('@/components/Prismic/Landings/Slices/TrustedBy.vue');
const VectorsGrid = () => import('@/components/Prismic/Landings/Slices/VectorsGrid.vue');
const Video = () => import('@/components/Prismic/Landings/Slices/Video.vue');
const HeroTabs = () => import('@/components/Prismic/Landings/Slices/HeroTabs.vue');
const PlansGetGraphics = () => import('@/components/Prismic/Landings/Slices/PlansGetGraphics.vue');
const PlansIconBlock = () => import('@/components/Prismic/Landings/Slices/PlansIconBlock.vue');
const PlansComparisonTable = () =>
  import('@/components/Prismic/Landings/Slices/PlansComparisonTable.vue');
const Button = () => import('@/components/Prismic/Landings/Slices/Button.vue');
const HowTo = () => import('@/components/Prismic/Landings/Slices/HowTo.vue');
const GridTabsExperience = () => import('@/components/Prismic/Landings/Slices/GridTabsExperience');
const Makers = () => import('@/components/Home/Makers.vue');
const YouCanFind = () => import('@/components/Prismic/Landings/Slices/YouCanFind.vue');
const Title = () => import('@/components/Prismic/Landings/Slices/Title.vue');
const Columns = () => import('@/components/Prismic/Landings/Slices/Columns.vue');
const Space = () => import('@/components/Prismic/Landings/Slices/Space.vue');
const Features = () => import('@/components/Prismic/Landings/Slices/Features.vue');
const ChooseExperience = () => import('@/components/Prismic/Landings/Slices/ChooseExperience.vue');
const BannerContent = () => import('@/components/Prismic/Landings/Slices/BannerContent.vue');

export default {
  name: 'SlicesBlock',
  components: {
    Carousel,
    CarouselCustom,
    CarouselHeader,
    CollectionsCarousel,
    FAQ,
    Header,
    ExtendedHeader,
    HeroHeader,
    HeroPlans,
    MockupSection,
    LeftRightImage,
    FindBestDesignsBlocks,
    NichesCarousel,
    Plans,
    PODs,
    InfoGrid,
    PurchasePlan,
    Testimonials,
    TextColumns,
    TrustedBy,
    SearchGridCarousel,
    SlicesSeparator,
    StylesCarousel,
    VectorsGrid,
    Video,
    HeroTabs,
    PlansComparisonTable,
    PlansGetGraphics,
    PlansIconBlock,
    Button,
    HowTo,
    GridTabsExperience,
    Makers,
    YouCanFind,
    Title,
    Columns,
    Space,
    Features,
    ChooseExperience,
    BannerContent
  },
  props: {
    slices: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      defaultMargin: 50,
      noMarginSlices: /carousel|space|title|tabs_grid_experience|home_header_default/
    };
  },
  methods: {
    margin(margin) {
      return margin || margin == 0 ? margin : this.defaultMargin;
    },
    marginTop(index, margin, sliceType) {
      return index != 0 && !this.noMarginSlices.test(sliceType) ? this.margin(margin) : 0;
    },
    marginBottom(margin, sliceType) {
      return !/carousel_header/.test(sliceType) && !this.noMarginSlices.test(sliceType)
        ? this.margin(margin)
        : 0;
    }
  }
};
</script>
